import { ICommentData } from 'models/comment';
import { IPaginator } from 'models/paginator';
import { IDictionary } from 'models/dictionary';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { ETransactionItemType } from 'constants/TransactionItemType';
import { Asset } from 'utils/assets';
import { EMarkingTypes } from 'constants/Markings';

export interface IInitialTransaction {
  request: {
    typeUniqueId: string;
    date: string;
    groupName?: string;
    ownerId: string;
    keeperId: string;
    countryOfImportUniqueId?: string;
    countryOfExportUniqueId?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
    firearms: [
      {
        frtId: string;
        specificationId: string;
        serialNumber: string;
        manufacturedAt: string;
      },
    ];
  };
  attachment?: string;
}

export interface ISearchFormValues {
  preset: string | undefined;
  transactionTypesUniqueIds: string[];
  transactionTimeFrom: string;
  transactionTimeTo: string;
  createTimeFrom: string;
  createTimeTo: string;
  legalityUniqueId: string;
  expirationTimeFrom: string;
  expirationTimeTo: string;
  ownerUniqueId: string;
  keeperUniqueId: string;
  ownerName: string;
  keeperName: string;
  asset: undefined | Asset;

  [key: string]: any;
}

export interface IShortTransaction {
  transactionUniqueId: string;
  typeUniqueId: string;
  transactionTime: string;
}

export interface ILastTransactionUniqueId {
  transactionUniqueId: string | null;
}

export enum ITransactionItemType {
  FIREARM = 'Firearm',
  ANCILLARY = 'Ancillary',
}

export interface ITransaction {
  transactionUniqueId: string;
  date: string;
  type: string;
  typeUniqueId: string;
  state: string;
  stateUniqueId: string;
  serialNumber: string;
  fireCycleId: string | null;
  owner: string;
  keeper: string;
  make: string;
  model: string;
  manufacturerUniqueId?: string;
  transactionItemType: ITransactionItemType;
  activityUniqueId: string;
  legality: string;
  legalityUniqueId: string;
}

export interface ITransactionResponse extends IPaginator {
  content: ITransaction[];
}

export interface ITransactionDetails {
  uniqueId: string;
  typeUniqueId: string;
  date: string;
  createdDate: string;
  createdBy: string;
  visible: boolean;
  owner: string;
  ownerId: string;
  ownerType: string;
  ownerVisibilityUniqueId: string;
  keeper: string;
  keeperId: string;
  keeperType: string;
  keeperVisibilityUniqueId: string;
  firearmGroupName: string | null;
  ancillaryGroupName: string | null;
  firearmsWithDifferentMakesOrModels: boolean;
  ancillariesWithDifferentART: boolean;
  ancillaryGroupUniqueId: string | null;
  firearmGroupUniqueId: string | null;
  initial: boolean;
  legalityUniqueId: any;
  expirationDate?: Date;
  countryOfExport?: IDictionary;
  countryOfImport?: IDictionary;
  importer?: string;
  activityUniqueId: string;
  latitude?: string;
  longitude?: string;
  address?: string;
  description?: string;
  transactionItemType: ETransactionItemType;
  licenceAuthorityIds: string[];
  anyElementInDisabledState: boolean;
  discardDisabled: boolean;
}

export interface ITransactionLocation {
  latitude?: string;
  longitude?: string;
  address?: string;
}

export interface ITransactionDetailsFirearm {
  uniqueId: string;
  serialNumber: string;
  fireCycleId: string;
  marking: string;
  makeUniqueId: string;
  modelUniqueId: string;
  actionUniqueId: string;
  typeUniqueId: string;
  caliberUniqueId: string;
  appliedOrImportMarking: string;
  frtUniqueId: string;
  frtId: string;
  specificationId: string;
  yearOfConfiscation: string;
  yearOfTransfer: string;
  manufacturedAt: string;
  objectUniqueId: string;
  customFields?: ILegalEntityCustomField[];
}

export interface ITransactionDetailsAncillary {
  uniqueId: string;
  artUniqueId: string;
  artId: string;
  ancillaryType: string;
  fireCycleId: string;
  serialNumber: string;
  ancillaryDictionaryValue: string;
  appliedOrImportMarking: string;
  productionDate: string;
  ammunitionType: string;
  accessoryType: string;
  componentType: string;
  manufacturer: string;
  calibre: string;
  lotNumber: string;
  batchNumber: string;
  ancillarySpecificType: string;
  marking: string;
  yearOfTransfer: string;
  yearOfConfiscation: string;
}

export interface ITransactionMarking {
  uniqueId: string;
  marking: string;
  markingType: EMarkingTypes;
  addedByTransaction: boolean;
  wasAssetImported: boolean;
}
export interface ITransactionDetailsMarkings {
  fireCycleId: string;
  serialNumber: string;
  previousSerialNumber: string;
  batchNumber: string;
  previousBatchNumber: string;
  lotNumber: string;
  previousLotNumber: string;
  yearOfConfiscation: string;
  yearOfTransfer: string;
  countryOfExport: string;
  markings: ITransactionMarking[];
  wasAssetImported: boolean;
}

export interface ILastTransaction {
  uniqueId: string;
  typeUniqueId: string;
  date: string;
  groupUniqueId?: string;
  ownerId?: string;
  keeperId?: string;
  description?: string;
  firearmIds: string[];
  ancillariesIds: string[];
  importer?: string;
  anyElementInDisabledState: boolean;
  discardDisabled: boolean;
}

export interface ITransactionDetailsAncillaryResponse extends IPaginator {
  content: ITransactionDetailsAncillary[];
}
export interface ITransactionDetailsMarkingsResponse extends IPaginator {
  content: ITransactionDetailsMarkings[];
}

export interface ITransactionDetailsFirearmsResponse extends IPaginator {
  content: ITransactionDetailsFirearm[];
}

export interface ITransactionDetailsCommentsResponse extends IPaginator {
  content: ICommentData[];
}

export interface ITransactionDetailsCommentsQuery {
  withHidden: boolean;
}
